import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { projectFilesQuery, removeProjectFile, uploadProjectFile } from "@/lib/api";
import { formatDate } from "@/lib/format";
import type { ProjectFileKind } from "@zworks/console-contracts";
import { SettingsFailure } from "./settings-feedback";

const ACCEPTED_BY_KIND: Record<ProjectFileKind, { mime: string; label: string }> = {
  pdf: { mime: "application/pdf", label: "PDF" },
  markdown: { mime: "text/markdown", label: "Markdown" },
  word: {
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    label: "Word",
  },
  yaml: { mime: "text/yaml", label: "YAML" },
  csv: { mime: "text/csv", label: "CSV" },
  image: { mime: "image/png,image/jpeg", label: "Image" },
};

const MAX_BYTES = 10 * 1024 * 1024;

function kindFor(file: File): ProjectFileKind {
  const name = file.name.toLowerCase();
  if (file.type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".md") || name.endsWith(".markdown")) return "markdown";
  if (name.endsWith(".docx") || name.endsWith(".doc")) return "word";
  if (name.endsWith(".yaml") || name.endsWith(".yml")) return "yaml";
  if (name.endsWith(".csv")) return "csv";
  return "image";
}

function indexBadge(state: string) {
  switch (state) {
    case "ready":
      return <Badge variant="success">Indexed</Badge>;
    case "indexing":
      return <Badge variant="info">Indexing</Badge>;
    case "unindexed":
      return <Badge variant="muted">Not indexed</Badge>;
    default:
      return <Badge variant="warning">Index failed</Badge>;
  }
}

/**
 * The Files section inside Project settings Artifacts: drag and drop upload with size and
 * type limits, the index state per file, and removal. Every kind feeds retrieval with
 * citations once its extracted text is indexed, including embedded image text.
 */
export function ProjectFilesSection({
  projectId,
  canEdit,
}: {
  projectId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const files = useQuery({ ...projectFilesQuery(projectId), retry: false });
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const listKey = projectFilesQuery(projectId).queryKey;

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const chunks: string[] = [];
      const CHUNK = 0x8000;
      for (let offset = 0; offset < bytes.length; offset += CHUNK) {
        chunks.push(
          String.fromCharCode(...bytes.subarray(offset, Math.min(offset + CHUNK, bytes.length))),
        );
      }
      return uploadProjectFile(projectId, {
        name: file.name,
        kind: kindFor(file),
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        contentBase64: btoa(chunks.join("")),
      });
    },
    onSuccess: (value) => {
      queryClient.setQueryData(listKey, [value, ...(files.data ?? [])]);
      setNotice(`${value.name} uploaded${value.indexState === "ready" ? " and indexed." : "."}`);
    },
    onSettled: () => setDragging(false),
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => removeProjectFile(projectId, fileId),
    onSuccess: (_, fileId) => {
      queryClient.setQueryData(
        listKey,
        (files.data ?? []).filter((row) => row.id !== fileId),
      );
    },
  });

  function accept(fileList: FileList | null) {
    setNotice(null);
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0]!;
    if (file.size > MAX_BYTES) {
      setNotice("The file exceeds the 10 MB limit. Split it and try again.");
      return;
    }
    upload.mutate(file);
  }

  if (files.isPending) {
    return (
      <>
        <h2 className="text-sm font-medium">Files</h2>
        <LoadingState label="Loading files" rows={3} />
      </>
    );
  }

  if (files.isError) {
    return (
      <>
        <h2 className="text-sm font-medium">Files</h2>
        <SettingsFailure error={files.error} retry={() => void files.refetch()} />
      </>
    );
  }

  const list = files.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-medium">Files</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Project documents for Zot and agents, up to 10 MB each. PDF, Markdown, Word, YAML, CSV,
          and images.
        </p>
      </div>
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {upload.error ? <SettingsFailure error={upload.error} /> : null}
      {remove.error ? <SettingsFailure error={remove.error} /> : null}
      {canEdit ? (
        <Card>
          <div
            role="button"
            tabIndex={0}
            aria-label="Upload a project file"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              accept(event.dataTransfer.files);
            }}
            className={`flex flex-col items-center gap-2 border-2 border-dashed p-6 text-center ${
              dragging ? "border-accent bg-muted/60" : "border-input"
            }`}
          >
            <p className="text-sm font-medium">
              {upload.isPending ? "Uploading…" : "Drop a file here or choose one"}
            </p>
            <p className="text-xs text-muted-foreground">
              PDF, Markdown (.md), Word (.docx), YAML, CSV, PNG, or JPEG · 10 MB maximum
            </p>
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              accept={Object.values(ACCEPTED_BY_KIND)
                .map((entry) => entry.mime)
                .join(",")}
              onChange={(event) => {
                accept(event.target.files);
                event.target.value = "";
              }}
            />
          </div>
        </Card>
      ) : null}
      <Card>
        <div className="border-b px-5 py-3">
          <h3 className="text-sm font-medium">Uploaded files</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Every role reads files. Only owners and developers upload or remove them.
          </p>
        </div>
        {list.length === 0 ? (
          <p className="px-5 py-3 text-sm text-muted-foreground">
            No files yet. Upload the documents Zot should cite.
          </p>
        ) : (
          <ul className="divide-y">
            {list.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{row.name}</span>
                  <span className="block truncate text-xs text-muted-foreground tabular-nums">
                    {ACCEPTED_BY_KIND[row.kind]?.label ?? row.kind} ·{" "}
                    {(row.sizeBytes / 1024).toFixed(1)} KB · {formatDate(row.createdAt)}
                    {row.uploaderName ? ` · ${row.uploaderName}` : ""}
                    {row.indexState === "unindexed" && row.indexError ? ` · ${row.indexError}` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  {indexBadge(row.indexState)}
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(row.id)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
